/*
  Self-serve pairs (CJ 2026-10-28, "Let customers self-serve combined mounts").

  Shared by the product page's "What goes in the other half?" box
  (snippets/rm-pair-box.liquid) and the custom form's "we already make this
  pair" shortcut (sections/custom-orders.liquid). Exposes window.rmPairs.

  The list of devices that can be paired comes from the print box's proof
  (rack-mounts-sku-api GET /pair-ready/public): only devices whose insert half
  and caged blank are both on the shelf and verified. It never carries a price
  or a variant id. Both are read live from Shopify when the buyer picks a
  device, so a price change, a renamed listing or an ended listing can never
  be offered from a stale copy. Anything that does not check out is simply not
  offered (fail closed).

  A pair is two real cart lines, each tagged with hidden line properties the
  print box reads (app/pair_intake.py in 3d-automated-printer):

    _pair_id     one id per panel
    _pair_role   device  = prints its insert half
                 partner = prints its caged blank
    _pair_with   the other line's SKU

  plus two visible ones the buyer sees in the cart, checkout and emails:
  "Your pair" (which device is on which side) and "This half".
*/
(function () {
  if (window.rmPairs) return;

  var CACHE_KEY = 'rm_pairs_v2';   // v2: rows carry the pair picture URLs
  var CACHE_MS = 5 * 60 * 1000;
  var listPromise = null;
  var listUrl = null;
  var FEE_SKU = 'PAIR-HEIGHT-MATCH';     // print box app/pair_intake.HEIGHT_MATCH_SKU

  function fmt(cents) {
    return (window.Shopify && window.Shopify.formatMoney)
      ? window.Shopify.formatMoney(cents)
      : '$' + (cents / 100).toFixed(2);
  }

  function track(name, fields) {
    try { if (window.rmTrack) window.rmTrack(name, fields || {}); } catch (e) { /* never blocks */ }
  }

  /* The ready list, cached for the tab for 5 minutes. Resolves to [] on any
     failure or a stale snapshot, which hides the box rather than guessing. */
  function list(url) {
    if (!url) return Promise.resolve([]);
    listUrl = url;
    if (listPromise) return listPromise;
    try {
      var hit = JSON.parse(sessionStorage.getItem(CACHE_KEY) || 'null');
      if (hit && hit.url === url && Date.now() - hit.at < CACHE_MS && Array.isArray(hit.devices)) {
        listPromise = Promise.resolve(hit.devices);
        return listPromise;
      }
    } catch (e) { /* private mode */ }
    listPromise = fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (body) {
        var devices = body && body.fresh && Array.isArray(body.devices) ? body.devices : [];
        try { sessionStorage.setItem(CACHE_KEY, JSON.stringify({ url: url, at: Date.now(), devices: devices })); } catch (e) { /* ignore */ }
        return devices;
      })
      .catch(function () { return []; });
    return listPromise;
  }

  /* Can `device` (prints its insert half) pair with `partner` (prints its caged
     blank)? Same rack height, the same seam family on the two halves that
     meet, opposite hands, and two different devices (two of one device is its
     own -2x variant). Mirrors pair_intake.check_pair on the print box. */
  function mates(device, partner, mixed) {
    if (!(device && partner && device.sku !== partner.sku && heightOk(device, partner, mixed))) return false;
    // A mixed-height panel rebuilds the shorter half on the taller one's seam and opposite
    // hand (CJ 2026-10-29: 1U devices are on the old seam, every 2U half is dovetail), so only
    // the taller side's own half has to exist; it is on the ready list, so it does.
    if (device.u !== partner.u) return !!(device.u > partner.u ? device.insert_hand : partner.cb_hand);
    return !!(device.insert_family && device.insert_family === partner.cb_family &&
      device.insert_hand && partner.cb_hand && device.insert_hand !== partner.cb_hand);
  }

  /* Same rack height, or (only when the store offers the $10 height match) a
     different one whose SHORTER device can be rebuilt at the taller height
     (CJ 2026-10-29). A Fusion-era design is never rebuilt, so it pairs at its
     own height only. */
  function heightOk(a, b, mixed) {
    if (!(a.u >= 1 && b.u >= 1)) return false;
    if (a.u === b.u) return true;
    return !!mixed && (a.u < b.u ? a : b).rebuildable === true;
  }

  /* The height-match note for a mixed panel, or null when both are one height. */
  function heightMatch(a, b, aLive, bLive) {
    if (a.u === b.u) return null;
    var short = a.u < b.u ? aLive : bLive, tall = a.u < b.u ? b : a;
    return shortTitle(short.title) + ' side made ' + tall.u + 'U tall to match';
  }

  /* The $10 height-match product, live (price and variant from Shopify). */
  var feeCache = {};
  function fee(handle) {
    if (!handle) return Promise.resolve(null);
    if (feeCache[handle]) return feeCache[handle];
    feeCache[handle] = fetch('/products/' + encodeURIComponent(handle) + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (p) {
        var v = p && (p.variants || []).filter(function (v) { return v.available && v.sku === FEE_SKU; })[0];
        return v ? { variant_id: v.id, price: v.price } : null;
      })
      .catch(function () { return null; })
      .then(function (row) { if (!row) delete feeCache[handle]; return row; });
    return feeCache[handle];
  }

  /* Which of the two sits on the left. */
  function sides(device, partner) {
    // In a mixed-height panel the shorter half is rebuilt to fit the taller one, so the
    // taller half's proven hand decides the sides.
    var deviceLeft = device.u < partner.u ? partner.cb_hand === 'right' : device.insert_hand === 'left';
    return deviceLeft ? { left: device, right: partner } : { left: partner, right: device };
  }

  /* The real joined row (CJ 2026-10-29): the device's half and the partner's caged
     blank, each drawn from the print file it ships today by one fixed camera
     (claude-brain scripts/pair-pictures), so the two stack into the exact panel.
     The ready list carries a URL only while the picture matches the current file.
     Two heights (CJ 2026-10-31): the taller side's own picture plus the shorter
     device's half drawn rebuilt at the taller U (img_device_at / img_partner_at).
     Null, and the panel keeps the product photos, when either picture is missing
     or the device half would sit on the left: the pictures are drawn with the
     device half on the right, and a picture is never mirrored. */
  function rowPictures(device, partner) {
    if (!device || !partner) return null;
    var tall = Math.max(device.u, partner.u);
    if (device.u === partner.u ? device.insert_hand !== 'right' : sides(device, partner).right !== device) return null;
    var d = abs(device.u === tall ? device.img_device : (device.img_device_at || {})[tall]);
    var p = abs(partner.u === tall ? partner.img_partner : (partner.img_partner_at || {})[tall]);
    return d && p ? { device: d, partner: p } : null;
  }
  function abs(path) {
    if (!path || typeof path !== 'string') return null;
    if (/^https:\/\//.test(path)) return path;
    if (path.charAt(0) !== '/' || !/^https:\/\//.test(listUrl || '')) return null;
    try { return new URL(path, listUrl).href; } catch (e) { return null; }
  }

  /* Every ready device that can share a panel with `me`, either way round, best
     first (CJ 2026-10-31, "in order of some priority", not A to Z): a device of
     this same height before one that needs the height match, then the best
     sellers (`rank` = 19" units sold in the last 180 days, from the ready list),
     then A to Z. */
  function partnersFor(me, devices, mixed) {
    return devices.filter(function (d) { return mates(me, d, mixed) || mates(d, me, mixed); })
      .sort(function (a, b) {
        return (a.u === me.u ? 0 : 1) - (b.u === me.u ? 0 : 1) ||
          (Number(b.rank) || 0) - (Number(a.rank) || 0) || String(a.title).localeCompare(String(b.title));
      });
  }

  /* A live Shopify variant for this exact SKU, or null. Looked up through the
     storefront's own search (SKU field) and the product's .js, so it is the
     price and availability the checkout will charge. A listing that is not a
     19" mount, or whose variant is sold out, is not offered. */
  var liveCache = {};
  function live(sku) {
    if (liveCache[sku]) return liveCache[sku];
    var q = '/search/suggest.json?q=' + encodeURIComponent(sku) +
      '&resources[type]=product&resources[limit]=6&resources[options][fields]=variants.sku';
    liveCache[sku] = fetch(q, { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (body) {
        var products = (body && body.resources && body.resources.results && body.resources.results.products) || [];
        var tries = products.slice(0, 6).map(function (p) {
          return fetch('/products/' + encodeURIComponent(p.handle) + '.js', { headers: { Accept: 'application/json' } })
            .then(function (r) { return r.ok ? r.json() : null; })
            .catch(function () { return null; });
        });
        return Promise.all(tries);
      })
      .then(function (products) {
        for (var i = 0; i < (products || []).length; i++) {
          var p = products[i];
          // A 19" listing: the ready list already proved this SKU is a modular half,
          // and not every such listing says "(Modular)" in its title.
          if (!p || !/19"/.test(p.title || '')) continue;
          for (var j = 0; j < (p.variants || []).length; j++) {
            var v = p.variants[j];
            if (v.sku === sku && v.available) {
              return {
                sku: sku, variant_id: v.id, price: v.price, title: p.title, url: '/products/' + p.handle,
                image: (v.featured_image && v.featured_image.src) || p.featured_image || null
              };
            }
          }
        }
        return null;
      })
      .catch(function () { return null; })
      .then(function (row) {
        // Keep a hit for the tab; forget a miss, so a blip can be retried by tapping again.
        if (!row) delete liveCache[sku];
        return row;
      });
    return liveCache[sku];
  }

  function pairId() {
    var a = new Uint8Array(6);
    (window.crypto || window.msCrypto).getRandomValues(a);
    return Array.prototype.map.call(a, function (b) { return ('0' + b.toString(16)).slice(-2); }).join('');
  }

  function shortTitle(t) {
    return String(t || '')
      .replace(/^19"\s*/, '')
      .replace(/\s*Rack Mount\b.*$/i, '')
      .replace(/\s+-\s+.*$/, '')
      .trim() || String(t || '');
  }

  /* The cart lines for `count` panels. `device` and `partner` are ready-list
     rows; `dv` and `pv` their live variants; `fv` the live height-match fee,
     added once per panel (same pair id, role fee) when the heights differ. */
  function lines(device, partner, dv, pv, count, fv) {
    var match = heightMatch(device, partner, dv, pv);
    if (match && !fv) throw new Error('a mixed-height panel needs the height-match fee');
    var s = sides(device, partner);
    var leftT = shortTitle((s.left === device ? dv : pv).title);
    var rightT = shortTitle((s.right === device ? dv : pv).title);
    var panel = 'Left: ' + leftT + ' | Right: ' + rightT;  // buyer-visible: "mount", never "panel" (CJ)
    var out = [];
    for (var n = 0; n < Math.max(1, count | 0); n++) {
      var id = pairId();
      out.push({
        id: dv.variant_id, quantity: 1, properties: {
          'Your pair': panel,
          'This half': s.left === device ? 'Left' : 'Right',
          _pair_id: id, _pair_role: 'device', _pair_with: partner.sku
        }
      });
      out.push({
        id: pv.variant_id, quantity: 1, properties: {
          'Your pair': panel,
          'This half': s.left === partner ? 'Left' : 'Right',
          _pair_id: id, _pair_role: 'partner', _pair_with: device.sku
        }
      });
      if (match) out.push({
        id: fv.variant_id, quantity: 1, properties: {
          'Your pair': panel, 'Height match': match,
          _pair_id: id, _pair_role: 'fee', _pair_with: ''
        }
      });
    }
    return out;
  }

  function add(items) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items })
    }).then(function (r) { if (!r.ok) throw new Error('add failed'); return r.json(); });
  }

  /* ── Matching typed devices to the list (custom form) ──────────────────── */

  var STOP = { rack: 1, mount: 1, mounts: 1, mounting: 1, modular: 1, '19': 1, '10': 1, inch: 1, 'in': 1,
    '1u': 1, '2u': 1, '3u': 1, u: 1, for: 1, with: 1, and: 1, the: 1, a: 1, w: 1, my: 1, of: 1,
    keystone: 1, jack: 1, jacks: 1, fits: 1, all: 1, series: 1, to: 1, one: 1, two: 1, panel: 1 };

  function tokens(text) {
    return String(text || '').toLowerCase().replace(/["'’]/g, '').replace(/(\d)\.(\d)/g, '$1$2')
      .replace(/[^a-z0-9+]+/g, ' ')
      .split(' ').filter(function (t) { return t && !STOP[t]; });
  }

  /* Words that say what kind of box it is, not which one. They neither have to
     be typed nor count as a match on their own. */
  var GENERIC = { 'switch': 1, router: 1, gateway: 1, firewall: 1, hub: 1, thin: 1, client: 1, pc: 1,
    pcs: 1, desktop: 1, server: 1, nas: 1, port: 1, ports: 1, poe: 1, 'poe+': 1, managed: 1, unmanaged: 1,
    gigabit: 1, network: 1, networking: 1, device: 1, box: 1, access: 1, point: 1, ap: 1, modem: 1,
    controller: 1, gen: 1, generation: 1, smart: 1, home: 1, media: 1, player: 1, streaming: 1, security: 1,
    amplifier: 1, amp: 1, dac: 1, dock: 1, thunderbolt: 1, fiber: 1, ont: 1, kvm: 1, audio: 1, tv: 1 };

  /* A speed, count or year, not a model: 10g, 2.5gbe, 1x, 2017. */
  function isSpec(t) { return /^\d+(g|gb|gbe|gbps|x)$/.test(t) || /^(19|20)\d\d$/.test(t); }
  function isModel(t) { return /\d/.test(t) && !isSpec(t); }

  /* Every way a title's words can be typed: each word, and runs of up to three
     joined ("tl sg108pe" or "tlsg108pe"). Never a fragment: "sg108" is not
     "sg108pe", those are two different switches. */
  function forms(words) {
    var out = {};
    for (var i = 0; i < words.length; i++) {
      var run = '';
      for (var j = i; j < Math.min(words.length, i + 3); j++) { run += words[j]; out[run] = 1; }
    }
    return out;
  }

  /* The single ready device a typed phrase names, or null when none, or more
     than one, fit. Strict on purpose: offering the wrong device is worse than
     offering none (the buyer just carries on to a quote).
       - every word typed (bar generic ones) is a whole word of the title;
       - a title with a model number needs one of them typed;
       - no other title word is left out except the brand (its first word or
         two: "Route10" is fine, "Nvidia Shield" is not the Shield TV Pro), and
         sibling models listed in one title ("TZ270, TZ370 & TZ470") need only
         the one the buyer has;
       - and the words fit no other device on the list, even loosely. */
  function fits(want, d, strict) {
    var have = tokens(shortTitle(d.title));
    var f = forms(have);
    if (!want.every(function (t) { return f[t]; })) return false;
    var covered = function (t) { return want.some(function (w) { return w === t || (w.length > t.length && w.indexOf(t) > -1); }); };
    var models = have.filter(isModel);
    if (models.length && !models.some(covered)) return false;
    if (!strict) return true;
    var typedModels = want.filter(isModel);
    var missing = 0, brandOnly = true;   // brandOnly: everything left out is the brand
    for (var i = 0; i < have.length; i++) {
      var t = have[i];
      if (GENERIC[t] || isSpec(t) || covered(t)) continue;
      var stem = t.replace(/\d.*$/, '');
      if (isModel(t) && stem.length >= 2 && typedModels.some(function (w) { return w.replace(/\d.*$/, '') === stem; })) continue;
      // A model's letter prefix written apart ("TL-SG108E" typed "SG108E").
      if (!/\d/.test(t) && t.length <= 3 && i + 1 < have.length && isModel(have[i + 1]) && covered(have[i + 1])) continue;
      missing++;
      if (i > 1 || isModel(t)) brandOnly = false;    // the brand is the title's first word or two
    }
    return (!missing || brandOnly) && (want.length >= 2 || isModel(want[0]));
  }

  function match(phrase, devices) {
    // A count ("1x") is never part of a name; a speed ("2.5g") is, when typed.
    var want = tokens(phrase).filter(function (t) { return !GENERIC[t] && !/^\d+x$/.test(t); });
    if (!want.length) return null;
    var hits = devices.filter(function (d) { return fits(want, d, true); });
    // The words must not also fit a sibling we make ("Fosi Audio V3" is the V3 Mono
    // too): the buyer could have either, so neither is offered.
    var loose = devices.filter(function (d) { return fits(want, d, false); });
    return hits.length === 1 && loose.length === 1 ? hits[0] : null;
  }

  /* ── Searching the picker (product page) ─────────────────────────────── */

  /* Forgiving where match() is strict: the buyer is choosing from the list, so a
     loose hit costs nothing and a miss loses the pair (CJ 2026-10-29, "the search
     is a key part of this"). Every typed word must hit the title, by any of:
       - a whole word (best), the start of a word, or anywhere in the title with
         its spaces and dashes taken out ("tplink", "sg108e", "nucbox");
       - another name the brand goes by ("ubiquiti" finds UniFi);
       - one slip of the keyboard in a word of 4+ letters ("netgaer", "minsforum").
     Hits rank by how well they hit, then in the order the list came in. */
  var ALIAS = { ubiquiti: ['unifi'], ubnt: ['ubiquiti', 'unifi'], ui: ['unifi', 'ubiquiti'], unifi: ['ubiquiti'],
    usw: ['unifi'], thinkcenter: ['thinkcentre'], tiny: ['thinkcentre'], nano: ['thinkcentre'],
    amplifier: ['amp'], firewall: ['firewalla', 'sonicwall'], kvm: ['pikvm'], homeassistant: ['assistant'],
    ha: ['assistant'], heos: ['denon'],
    zima: ['zimaboard'], gmk: ['gmktec'], hdhr: ['hdhomerun'], msi: ['cubi'] };

  // Edit distance with a swapped pair counted as one slip, stopping once past `max`.
  function slips(a, b, max) {
    if (Math.abs(a.length - b.length) > max) return max + 1;
    var prev2 = null, prev = [], cur, i, j;
    for (j = 0; j <= b.length; j++) prev[j] = j;
    for (i = 1; i <= a.length; i++) {
      cur = [i];
      var best = i;
      for (j = 1; j <= b.length; j++) {
        var c = a[i - 1] === b[j - 1] ? 0 : 1;
        cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + c);
        if (prev2 && i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) cur[j] = Math.min(cur[j], prev2[j - 2] + 1);
        if (cur[j] < best) best = cur[j];
      }
      if (best > max) return max + 1;
      prev2 = prev; prev = cur;
    }
    return prev[b.length];
  }

  // How well one typed word hits a title: 4 whole word, 3 word start, 2 inside or
  // by another name, 1 one slip off, 0 not at all.
  function hit(w, words, joined) {
    var i, best = 0;
    for (i = 0; i < words.length; i++) {
      if (words[i] === w) return 4;
      if (words[i].indexOf(w) === 0) best = 3;
    }
    if (best) return best;
    // Whole words written together ("tlsg105" is TL-SG105, not TL-SG105E).
    if (forms(words)[w]) return 4;
    // Inside the title only from 3 letters (or any number): "ui" is not in "build".
    if ((w.length >= 3 || /\d/.test(w)) && joined.indexOf(w) > -1) return 2;
    var al = ALIAS[w] || [];
    for (i = 0; i < al.length; i++) if (joined.indexOf(al[i]) > -1) return 2;
    if (w.length < 3 || /^\d+$/.test(w)) return 0;
    var max = w.length >= 7 ? 2 : 1;
    for (i = 0; i < words.length; i++) {
      var t = words[i];
      // Three letters slip only into a longer word with the same first letter ("wim").
      if (t.length < 3 || (w.length === 3 && (t.length < 4 || t[0] !== w[0]))) continue;
      // The whole word, or its start when the buyer is still typing.
      if (slips(w, t, max) <= max) return 1;
      for (var n = w.length - 1; n <= w.length + 1; n++) {
        if (n < t.length && n >= 3 && slips(w, t.slice(0, n), max) <= max) return 1;
      }
    }
    return 0;
  }

  var SOFT = { mini: 1, streamer: 1, stream: 1, wifi: 1, wireless: 1, computer: 1, receiver: 1, headphone: 1, speaker: 1 };
  function soft(w) { return !!(GENERIC[w] || SOFT[w]); }

  function find(q, devices) {
    var want = tokens(q);
    // The list arrives in its priority order (partnersFor); a tie in score keeps it.
    var all = (devices || []).slice();
    if (!want.length) return all;
    var specific = want.some(function (w) { return !soft(w); });
    var scored = [];
    all.forEach(function (d) {
      var words = tokens(d.title), joined = words.join(''), score = 0;
      for (var i = 0; i < want.length; i++) {
        var h = hit(want[i], words, joined);
        // A word for the kind of box ("mini pc", "switch") only helps rank: plenty
        // of titles never say it. Typed alone, it still has to hit.
        if (!h && !(soft(want[i]) && specific)) return;
        score += h;
      }
      // What was typed, read as one run: a model written apart ("GS-308") or the
      // name in its own order ("Geekom Mini IT13" over "Geekom IT13 Max").
      var run = want.join('');
      if (want.length > 1 && words.indexOf(run) > -1) score += 3;
      else if (want.length > 1 && joined.indexOf(run) > -1) score += 2;
      // Then the title with the fewest words the buyer did not type.
      scored.push({ d: d, s: score * 100 - words.length });
    });
    // A stable sort is not promised on older phones: keep the list order inside a score by hand.
    return scored.map(function (x, i) { x.i = i; return x; })
      .sort(function (a, b) { return b.s - a.s || a.i - b.i; })
      .map(function (x) { return x.d; });
  }

  /* Split what a buyer typed into device phrases: "UCG Max and SG108, Pi 5". */
  function phrases(text) {
    return String(text || '').split(/\s*(?:\+|&|,|;|\/|\n|\band\b|\bwith\b|\bplus\b|\bnext to\b|\bbeside\b)\s*/i)
      .map(function (s) { return s.trim(); })
      .filter(function (s) { return s.length >= 3; });
  }

  window.rmPairs = {
    list: list, mates: mates, sides: sides, partnersFor: partnersFor, live: live, rowPictures: rowPictures,
    heightMatch: heightMatch, fee: fee,
    lines: lines, add: add, match: match, find: find, phrases: phrases, tokens: tokens,
    shortTitle: shortTitle, fmt: fmt, track: track
  };
})();
